import { postWithAuth } from '@/lib/httpAdapter';
import apiUrl from './api';

export type ExpiresIn = '24h' | '48h' | '7d' | '30d' | 'never';

export type InviteRole = 'member' | 'admin';

export interface InviteTokenResponse {
  Token: string | null;
  ExpiresAt: string | null;
  ExpiresIn: ExpiresIn | null;
  Role: string | null;
  // Team joiners are placed on; null = no team.
  TeamUUID: string | null;
  TeamName: string | null;
  Error: string | null;
}

// Which of the caller's links: links are keyed on (creator, role, team).
// teamUuid picks the team for an Owner; the backend ignores it for an
// Admin, whose links are always their own team. Omitted = no team.
export interface InviteLinkKey {
  role: InviteRole;
  teamUuid?: string;
}

const linkKeyBody = (key: InviteLinkKey) => ({
  Role: key.role,
  ...(key.teamUuid ? { TeamUUID: key.teamUuid } : {}),
});

export interface ValidateInviteTokenResponse {
  Valid: boolean;
  OrgUUID: string | null;
  OrgName: string | null;
  NumOfMembers: number | null;
  InviterName: string | null;
  InviterImage: string | null;
  OrgImage: string | null;
  Role: string | null;
  TeamUUID: string | null;
  TeamName: string | null;
  Error: string | null;
}

export interface InviteTokenUse {
  Email: string;
  UserUUID: string;
  UsedAt: string;
  Status: 'pending' | 'joined';
}

export interface InviteTokenUsesResponse {
  Uses: InviteTokenUse[];
  Error: string | null;
}

export const getOrCreateInviteToken = async (
  orgUUID: string,
  userUUID: string,
  expiresIn: ExpiresIn = '48h',
  regenerate = false,
  role: InviteRole = 'member',
  // false = read-only fetch; never mints a token as a side effect.
  createIfMissing = true,
  teamUuid?: string
): Promise<InviteTokenResponse> => {
  const res = await postWithAuth(`${apiUrl}getOrCreateInviteToken/`, {
    OrgUUID: orgUUID,
    UserUUID: userUUID,
    ExpiresIn: expiresIn,
    Regenerate: regenerate,
    CreateIfMissing: createIfMissing,
    ...linkKeyBody({ role, teamUuid }),
  });
  return JSON.parse(await res.json());
};

// Changes one link's expiry in place, link string unchanged. The key only
// selects the link: role and team are never edited in place.
export const updateInviteToken = async (
  orgUUID: string,
  userUUID: string,
  key: InviteLinkKey,
  changes: { expiresIn: ExpiresIn }
): Promise<InviteTokenResponse> => {
  const res = await postWithAuth(`${apiUrl}updateInviteToken/`, {
    OrgUUID: orgUUID,
    UserUUID: userUUID,
    ExpiresIn: changes.expiresIn,
    ...linkKeyBody(key),
  });
  return JSON.parse(await res.json());
};

// Revokes the one link for this key; the caller's other links stay live.
export const revokeInviteToken = async (
  orgUUID: string,
  userUUID: string,
  key: InviteLinkKey
): Promise<{ Error: string | null }> => {
  const res = await postWithAuth(`${apiUrl}revokeInviteToken/`, {
    OrgUUID: orgUUID,
    UserUUID: userUUID,
    ...linkKeyBody(key),
  });
  return JSON.parse(await res.json());
};

// Pre-auth: the join page calls this before the user has a JWT.
export const validateInviteToken = async (
  token: string
): Promise<ValidateInviteTokenResponse> => {
  const res = await fetch(
    `${apiUrl}validateInviteToken/?token=${encodeURIComponent(token)}`
  );
  if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
  const data: unknown = await res.json();
  return (
    typeof data === 'string' ? JSON.parse(data) : data
  ) as ValidateInviteTokenResponse;
};

export const fetchInviteTokenUses = async (
  orgUUID: string,
  userUUID: string
): Promise<InviteTokenUsesResponse> => {
  const res = await postWithAuth(`${apiUrl}fetchInviteTokenUses/`, {
    OrgUUID: orgUUID,
    UserUUID: userUUID,
  });
  return JSON.parse(await res.json());
};
